import { useState, useEffect } from 'react';
import { 
  Users, Calendar, AlertTriangle, MessageSquare, FileText, 
  UserPlus, CheckCircle2, Activity, Plus, Lightbulb, Upload, MapPin, Search, Eye,
  Home,
  ArrowLeft
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../ui/table';
import { PatientDetailsView } from '../PatientDetailsView';
import { ScheduleAppointmentModal } from './ScheduleAppointmentModal';
import { AppointmentsTab } from './AppointmentsTab';
import { RegisterPatientModal } from '../shared/RegisterPatientModal';
import { BackendHealthCheck } from '../common/BackendHealthCheck';
import { UploadDischargeSummaryModal } from './UploadDischargeSummaryModal';
import { apiClient } from '../../lib/api-client';
import { toast } from 'sonner';
import { HomeNurseTasksViewCompact } from '../doctor/HomeNurseTasksViewCompact';

interface PhysicianAssistantDashboardProps {
  onNavigate?: (view: string) => void;
}

type TabType = 'overview' | 'patientList' | 'appointments' | 'newPatients' | 'alerts' | 'chat';

export function PhysicianAssistantDashboard({ onNavigate }: PhysicianAssistantDashboardProps) {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [selectedPatient, setSelectedPatient] = useState<any | null>(null);
  const [selectedPatientNurse, setSelectedPatientNurse] = useState<any | null>(null);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('');
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isRegisterPatientModalOpen, setIsRegisterPatientModalOpen] = useState(false);
  const [isUploadDischargeSummaryModalOpen, setIsUploadDischargeSummaryModalOpen] = useState(false);
  const [selectedPatientForUpload, setSelectedPatientForUpload] = useState<any | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<'list' | 'details' | 'adherence-detail' | 'nurse-tasks'>('list');
  

  useEffect(() => {
    loadDashboardData();
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      const response = await apiClient.get('/auth/me');
      setUserName(`${response.first_name} ${response.last_name}`);
    } catch (error: any) {
      console.error('Failed to load user profile:', error);
      // Don't show error toast for profile load - just log it
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/physician-assistants/dashboard');
      setDashboardData(response);
    } catch (error: any) {
      console.error('Failed to load PA dashboard:', error);
      const errorMessage = error.message || 'Failed to load dashboard data';
      setError(errorMessage);
      
      // Show specific error message
      if (errorMessage.includes('Cannot connect to server')) {
        toast.error('Backend server is not responding. Please check if the server is running.', {
          duration: 5000,
        });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };
 
  if (selectedPatient) {
    return (
      <PatientDetailsView
        patient={selectedPatient}
        onBack={() => setSelectedPatient(null)}
        userRole="PHYSICIAN_ASSISTANT"
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#D4F0F8] p-4 flex items-center justify-center">
        <div className="text-primary">Loading dashboard...</div>
      </div>
    );
  }

  // Show error state if backend connection failed
  if (error && !dashboardData) {
    return (
      <BackendHealthCheck 
        error={error} 
        onRetry={loadDashboardData}
        display="page"
      />
    );
  }

  // Calculate badge counts
  const newPatientsCount = dashboardData?.new_patients?.length || 0;
  const alertsCount = (dashboardData?.flagged_vitals?.length || 0) + (dashboardData?.high_risk_alerts_total || 0);
  const unreadMessagesCount = dashboardData?.unread_messages_count || 0;

  // Get new patients (patients created in last 7 days)
  const newPatients = dashboardData?.all_patients?.filter((p: any) => {
    // Filter logic for new patients - you can adjust this
    return true; // For now, showing all as potentially new
  }).slice(0, 5) || [];

  const redFlagPatients = dashboardData?.flagged_vitals || [];
  const todayAppointments = dashboardData?.appointments_today || [];
  const allPatients = dashboardData?.all_patients || [];
  const alerts = [...(dashboardData?.flagged_vitals || []), ...(dashboardData?.pending_reviews || [])];

  // Filter patients based on search query
  const filteredPatients = allPatients.filter((patient: any) => {
    const query = searchQuery.toLowerCase();
    return (
      patient.name?.toLowerCase().includes(query) ||
      patient.umr_no?.toLowerCase().includes(query) ||
      patient.age?.toString().includes(query)
    );
  });

  const handleViewHomeNurseTasks = (patient: any) => {
    if (!patient?.id) {
      toast.error('Unable to load home nurse tasks. Patient ID is missing.');
      return;
    }
    setSelectedPatientNurse(patient);
    setActiveView('nurse-tasks');
    toast.info(`Loading ${patient.name || 'patient'}'s home nurse tasks...`);
  };

  const handleBackToList = () => {
    setSelectedPatient(null);
    setActiveView("list");
  };

  // Helper function to get SpO2 badge styling
  const getSpO2Badge = (spo2: number | undefined) => {
    if (!spo2) return { text: 'N/A', className: 'bg-gray-100 text-gray-600' };
    if (spo2 >= 95) return { text: `${spo2}%`, className: 'bg-[#14CAF4] text-white' };
    if (spo2 >= 90) return { text: `${spo2}%`, className: 'bg-accent text-white' };
    return { text: `${spo2}%`, className: 'bg-red-100 text-red-700' };
  };

  // Helper function to get Spirometry badge styling
  const getSpirometryBadge = (value: string | undefined) => {
    if (!value) return { text: 'N/A', className: 'bg-gray-100 text-gray-600' };
    if (value === 'Healthy') return { text: value, className: 'bg-[#14CAF4] text-white' };
    if (value === 'Warning') return { text: value, className: 'bg-accent text-white' };
    if (value === 'Critical') return { text: value, className: 'bg-red-100 text-red-700' };
    return { text: value, className: 'bg-gray-100 text-gray-600' };
  };

  // Helper function to get Status badge styling
  const getStatusBadge = (status: string | undefined) => {
    if (!status) return { text: 'N/A', className: 'bg-gray-100 text-gray-600' };
    if (status === 'On Track') return { text: status, className: 'bg-green-100 text-green-700 border-green-300' };
    if (status === 'Excellent') return { text: status, className: 'bg-orange-100 text-orange-700 border-orange-300' };
    if (status === 'At Risk') return { text: status, className: 'bg-red-100 text-red-700 border-red-300' };
    return { text: status, className: 'bg-gray-100 text-gray-600' };
  };

  // Home Nurse Tasks View
  if (activeView === 'nurse-tasks' && selectedPatientNurse) {
    return (
      <div className="min-h-screen bg-[#D4F0F8]">
        <div className="bg-white border-b border-border sticky top-0 z-40 shadow-sm">
          <div className="px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={handleBackToList} size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Back to Patient List</span>
                <span className="sm:hidden">Back</span>
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-primary">{selectedPatientNurse.name || 'Patient'}</h1>
                <p className="text-sm text-muted-foreground">Home Nurse Tasks</p>
              </div>
            </div>
          </div>
        </div>
        <div className="px-4 md:px-6 lg:px-8 py-6">
          <HomeNurseTasksViewCompact
            patientId={selectedPatientNurse?.id?.toString() || '0'}
            patientName={selectedPatientNurse?.name || 'Patient'}
            patientAge={selectedPatientNurse?.age}
            patientGender={selectedPatientNurse?.gender}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFB] pb-20">
      {/* ✅ REMOVED: Duplicate header - App.tsx already renders the global header */}
      {/* Dashboard Title Bar */}
      <div className="bg-white border-b border-gray-200 p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl sm:text-2xl text-primary">Physician Assistant Dashboard</h1>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">Welcome, {userName}</p>
            </div>
            <Button
              onClick={() => setIsRegisterPatientModalOpen(true)}
              className="bg-primary hover:bg-primary/90 gap-2"
              size="sm"
            >
              <UserPlus className="w-4 h-4" />
              <span className="hidden sm:inline">Register New Patient</span>
              <span className="sm:hidden">Register</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1 overflow-x-auto py-2">
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap ${
                activeTab === 'overview' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-foreground hover:bg-[#e5e5e3]'
              }`}
            >
              <CheckCircle2 className="w-4 h-4" />
              <span className="hidden sm:inline">Overview</span>
            </button>
            
            <button
              onClick={() => setActiveTab('patientList')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap ${
                activeTab === 'patientList' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-foreground hover:bg-[#e5e5e3]'
              }`}
            >
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Patient List</span>
            </button>
            
            <button
              onClick={() => setActiveTab('appointments')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap ${
                activeTab === 'appointments' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-foreground hover:bg-[#e5e5e3]'
              }`}
            >
              <Calendar className="w-4 h-4" />
              <span className="hidden sm:inline">Appointments</span>
            </button>
            
            <button
              onClick={() => setActiveTab('newPatients')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap relative ${
                activeTab === 'newPatients' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-foreground hover:bg-[#e5e5e3]'
              }`}
            >
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">New Patients</span>
              {newPatientsCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-accent text-white rounded-full flex items-center justify-center text-xs">
                  {newPatientsCount}
                </span>
              )}
            </button>
            
            <button
              onClick={() => setActiveTab('alerts')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap relative ${
                activeTab === 'alerts' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-foreground hover:bg-[#e5e5e3]'
              }`}
            >
              <AlertTriangle className="w-4 h-4" />
              <span className="hidden sm:inline">Alerts</span>
              {alertsCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs">
                  {alertsCount}
                </span>
              )}
            </button>
            
            <button
              onClick={() => onNavigate?.('messaging')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap relative ${
                activeTab === 'chat' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-foreground hover:bg-[#e5e5e3]'
              }`}
            >
              <MessageSquare className="w-4 h-4" />
              <span className="hidden sm:inline">Chat</span>
              {unreadMessagesCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#14CAF4] text-white rounded-full flex items-center justify-center text-xs">
                  {unreadMessagesCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Total Patients</p>
                      <h3 className="text-3xl text-primary">{dashboardData?.stats?.total_patients || 0}</h3>
                    </div>
                    <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
                      <Users className="w-6 h-6 text-primary" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Red Flag Patients</p>
                      <h3 className="text-3xl text-red-500">{redFlagPatients.length}</h3>
                    </div>
                    <div className="w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center">
                      <AlertTriangle className="w-6 h-6 text-red-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card 
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setActiveTab('appointments')}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Appointments</p>
                      <h3 className="text-3xl text-[#14CAF4]">{dashboardData?.stats?.total_appointments || 0}</h3>
                      <p className="text-xs text-muted-foreground mt-1">Today: {todayAppointments.length}</p>
                    </div>
                    <div className="w-12 h-12 rounded-lg bg-cyan-50 flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-[#14CAF4]" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">New Patients</p>
                      <h3 className="text-3xl text-accent">{newPatientsCount}</h3>
                    </div>
                    <div className="w-12 h-12 rounded-lg bg-orange-50 flex items-center justify-center">
                      <UserPlus className="w-6 h-6 text-accent" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Action Buttons Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setIsRegisterPatientModalOpen(true)}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-primary mb-1">Register New Patient</h3>
                      <p className="text-sm text-muted-foreground">
                        Register new patients into the Pulmonology Spectrum system.
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <UserPlus className="w-6 h-6 text-primary" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="hover:shadow-md transition-shadow cursor-pointer bg-accent/10" onClick={() => setIsScheduleModalOpen(true)}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-accent mb-1">Schedule Appointment</h3>
                      <p className="text-sm text-muted-foreground">
                        Schedule appointments for patients with doctors.
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-6 h-6 text-accent" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Info Card */}
            <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-primary/20">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Lightbulb className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg text-primary mb-2">Physician Assistant Dashboard</h3>
                    <p className="text-sm text-muted-foreground">
                      Assist in patient consultations, manage appointments, and support clinical documentation.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            {allPatients.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Recent Patient Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Patient</TableHead>
                        <TableHead>UMR No</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Adherence</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allPatients.slice(0, 5).map((patient: any) => (
                        <TableRow key={patient.id}>
                          <TableCell>{patient.name}</TableCell>
                          <TableCell>{patient.umr_no}</TableCell>
                          <TableCell>
                            <Badge variant={patient.status === 'ACTIVE' ? 'default' : 'secondary'}>
                              {patient.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className={patient.adherence >= 70 ? 'text-green-600' : 'text-red-600'}>
                              {patient.adherence}%
                            </span>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setSelectedPatient(patient)}
                            >
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {activeTab === 'patientList' && (
          <div className="space-y-6">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search patients by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Patient Overview Header */}
            <div>
              <h3 className="text-primary">
                Patient Overview ({filteredPatients.length} patients)
              </h3>
            </div>
            
            {/* Patient Table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-sm">Patient Name</TableHead>
                        <TableHead className="text-sm">Age</TableHead>
                        <TableHead className="text-sm">SpO2</TableHead>
                        <TableHead className="text-sm">Spirometry</TableHead>
                        <TableHead className="text-sm">Exercise Tolerance</TableHead>
                        <TableHead className="text-sm">Last Assessment</TableHead>
                        <TableHead className="text-sm">Status</TableHead>
                        <TableHead className="text-sm text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredPatients.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                            {searchQuery ? `No patients found matching "${searchQuery}"` : 'No patients assigned yet'}
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredPatients.map((patient: any) => {
                          const spo2Badge = getSpO2Badge(patient.last_spo2);
                          const spirometryBadge = getSpirometryBadge(patient.spirometry_status || 'Healthy');
                          const statusBadge = getStatusBadge(patient.patient_status || 'On Track');

                          return (
                            <TableRow key={patient.id} className="hover:bg-gray-50">
                              <TableCell className="py-4">
                                <span className="text-sm">{patient.name}</span>
                              </TableCell>
                              <TableCell className="py-4">
                                <span className="text-sm">{patient.age}</span>
                              </TableCell>
                              <TableCell className="py-4">
                                <Badge className={`${spo2Badge.className} rounded-md px-2 py-1 text-xs`}>
                                  {spo2Badge.text}
                                </Badge>
                              </TableCell>
                              <TableCell className="py-4">
                                <Badge className={`${spirometryBadge.className} rounded-md px-2 py-1 text-xs`}>
                                  {spirometryBadge.text}
                                </Badge>
                              </TableCell>
                              <TableCell className="py-4">
                                <span className="text-sm text-muted-foreground">
                                  {patient.exercise_tolerance || 'Good'}
                                </span>
                              </TableCell>
                              <TableCell className="py-4">
                                <span className="text-sm text-muted-foreground">
                                  {patient.last_session || '2025-10-20'}
                                </span>
                              </TableCell>
                              <TableCell className="py-4">
                                <Badge className={`${statusBadge.className} rounded-md px-2 py-1 text-xs border`}>
                                  {statusBadge.text}
                                </Badge>
                              </TableCell>
                              <TableCell className="py-4 text-center">
                                <div className="flex gap-2 justify-center">
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="text-primary"
                                    onClick={() => setSelectedPatient(patient)}
                                  >
                                    <Eye className="w-4 h-4 mr-1" />
                                    View Details
                                  </Button>
                                  <Button  
                                    variant="ghost" 
                                    size="sm" 
                                    className="text-[#14CAF4]"
                                    onClick={() => handleViewHomeNurseTasks(patient)}
                                  >
                                    <Home className="w-4 h-4 mr-1" />
                                    Home Nurse Tasks
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'appointments' && (
          <AppointmentsTab
            onRefresh={loadDashboardData}
          />
        )}

        {activeTab === 'newPatients' && (
          <div className="space-y-6">
            {/* Header with Title and Badge */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl text-primary">New Patients Onboarding</h2>
                <p className="text-sm text-muted-foreground mt-1">Upload discharge summary for new patients</p>
              </div>
              {newPatientsCount > 0 && (
                <Badge className="bg-accent text-white px-3 py-1">
                  Pending
                </Badge>
              )}
            </div>

            {/* Patient Cards Grid */}
            {newPatients.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-muted-foreground">No new patients pending discharge summary</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {newPatients.map((patient: any) => {
                  // Generate initials from patient name
                  const getInitials = (name: string) => {
                    return name
                      .split(' ')
                      .map(n => n[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2);
                  };

                  return (
                    <Card key={patient.id} className="hover:shadow-lg transition-shadow border border-gray-200">
                      <CardContent className="p-6">
                        {/* Patient Avatar and Info */}
                        <div className="flex items-start gap-4 mb-4">
                          <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-white flex-shrink-0">
                            <span className="text-sm">{getInitials(patient.name)}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-primary truncate">{patient.name}</h3>
                            <p className="text-sm text-muted-foreground">{patient.age} years</p>
                          </div>
                        </div>

                        {/* Hospital Info */}
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                          <MapPin className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{patient.hospital || 'Apollo Hospitals'}</span>
                        </div>

                        {/* Status */}
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                          <FileText className="w-4 h-4 flex-shrink-0" />
                          <span>Discharge summary pending</span>
                        </div>

                        {/* Upload Button */}
                        <Button
                          className="w-full bg-primary hover:bg-primary/90 gap-2"
                          onClick={() => {
                            setSelectedPatientForUpload(patient);
                            setIsUploadDischargeSummaryModalOpen(true);
                          }}
                        >
                          <Upload className="w-4 h-4" />
                          Upload Discharge Summary
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'alerts' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl text-primary">Active Alerts ({alertsCount})</h2>
            </div>

            <div className="grid gap-4">
              {alerts.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <AlertTriangle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No active alerts</p>
                  </CardContent>
                </Card>
              ) : (
                alerts.map((alert: any, index: number) => (
                  <Card key={index} className="border-l-4 border-l-red-500">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                          </div>
                          <div>
                            <h3 className="font-medium">{alert.name}</h3>
                            <p className="text-sm text-muted-foreground">UMR: {alert.umr_no}</p>
                            {alert.alertType && (
                              <Badge variant="destructive" className="mt-2">
                                {alert.alertType}
                              </Badge>
                            )}
                            {alert.last_spo2 && (
                              <p className="text-sm mt-2">SpO2: {alert.last_spo2}%</p>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedPatient(alert)}
                        >
                          Review
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'chat' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl text-primary">Messages</h2>
            </div>

            <Card>
              <CardContent className="p-8 text-center">
                <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">Chat functionality coming soon</p>
                <Button
                  variant="outline"
                  onClick={() => onNavigate?.('chat')}
                >
                  Go to Messages
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Schedule Appointment Modal */}
      <ScheduleAppointmentModal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        onSuccess={() => {
          loadDashboardData(); // Reload appointments after scheduling
        }}
      />

      {/* Register Patient Modal */}
      <RegisterPatientModal
        open={isRegisterPatientModalOpen}
        onOpenChange={setIsRegisterPatientModalOpen}
        onSuccess={() => {
          loadDashboardData(); // Reload patient list after registration
        }}
        userRole="physician_assistant"
      />

      {/* Upload Discharge Summary Modal */}
      <UploadDischargeSummaryModal
        isOpen={isUploadDischargeSummaryModalOpen}
        onClose={() => {
          setIsUploadDischargeSummaryModalOpen(false);
          setSelectedPatientForUpload(null);
        }}
        patient={selectedPatientForUpload}
        onSuccess={() => {
          loadDashboardData(); // Reload patient list after upload
          setIsUploadDischargeSummaryModalOpen(false);
          setSelectedPatientForUpload(null);
        }}
      />
    </div>
  );
}